import * as THREE from 'three';

import { SWISS_PROJECTION } from './swiss-projection.js';
import { environment } from '../../../environments/environment.js';
import { LruCache } from '../../shared/lru-cache.js';
import type { GeoBounds, TerrainTileMesh, TileCoord } from '../../types/terrain-types.js';

interface TextureCacheEntry {
  cacheKey: string;
  texture: THREE.Texture;
  wmtsBounds: GeoBounds;
  refCount: number;
  cached: boolean;
}

export interface WmtsTextureResult {
  texture: THREE.Texture;
  wmtsBounds: GeoBounds;
  cacheKey: string;
}

export const WMTS_TEXTURE_CACHE_ENTRIES = 500;

const TILE_SIZE = 256;

/**
 * Fetches, stitches and caches WMTS imagery used as terrain tile textures.
 *
 * Owns the texture LRU cache, the reference counting that keeps a composite
 * alive while tiles use it, and the semaphore that bounds concurrent image
 * requests so flying the camera does not exhaust the per-host connection pool.
 */
export class WmtsTextureProvider {
  readonly baseUrl: string;
  readonly layerMap: string;
  readonly layerAerial: string;
  readonly format = 'jpeg';
  layer: string;

  private readonly cache: LruCache<string, TextureCacheEntry>;
  private readonly liveEntries = new Map<THREE.Texture, TextureCacheEntry>();
  private cacheGeneration = 0;

  private readonly imageQueue: (() => void)[] = [];
  private activeImageLoads = 0;
  private static readonly MAX_CONCURRENT_IMAGES = 12;

  /**
   * @param maxAnisotropy Resolved lazily because the WebGL renderer may not
   * exist yet when the provider is constructed.
   */
  constructor(private readonly maxAnisotropy: () => number) {
    this.baseUrl = environment.terrain.wmtsBaseUrl;
    this.layerMap = environment.terrain.wmtsLayerMap;
    this.layerAerial = environment.terrain.wmtsLayerAerial;
    this.layer = this.layerMap;

    this.cache = new LruCache<string, TextureCacheEntry>(WMTS_TEXTURE_CACHE_ENTRIES, entry => {
      this.retire(entry);
    });
  }

  /**
   * Name of the layer currently used for new texture requests.
   */
  getLayerName(): 'map' | 'aerial' {
    return this.layer === this.layerAerial ? 'aerial' : 'map';
  }

  /**
   * Switch the active layer. Returns false when the layer is unchanged, so the
   * caller can skip an expensive reload of every tile texture.
   */
  setLayerName(layer: string): boolean {
    const next = layer === 'aerial' ? this.layerAerial : this.layerMap;
    if (next === this.layer) {
      return false;
    }
    this.layer = next;
    this.clear();
    return true;
  }

  /**
   * Load the composite WMTS texture covering a terrain tile.
   */
  async loadTexture(
    tileBounds: GeoBounds,
    terrainZoom: number,
    maxZoom: number,
    signal?: AbortSignal,
  ): Promise<WmtsTextureResult | null> {
    if (signal?.aborted) {
      return null;
    }
    const generation = this.cacheGeneration;
    const layer = this.layer;

    // Request the imagery at the same zoom as the terrain tile, matching the
    // official swisstopo viewer and keeping the number of stitched imagery
    // tiles per terrain tile small.
    const zoom = Math.min(terrainZoom, maxZoom);

    const swTile = this.lonLatToWebMercatorTile(tileBounds.west, tileBounds.south, zoom);
    const neTile = this.lonLatToWebMercatorTile(tileBounds.east, tileBounds.north, zoom);

    const minX = Math.min(swTile.x, neTile.x);
    const maxX = Math.max(swTile.x, neTile.x);
    const minY = Math.min(swTile.y, neTile.y);
    const maxY = Math.max(swTile.y, neTile.y);

    const cacheKey = `composite-${zoom}-${minX}-${minY}-${maxX}-${maxY}`;
    const cached = this.cache.get(cacheKey);
    if (cached) {
      cached.refCount++;
      return { texture: cached.texture, wmtsBounds: cached.wmtsBounds, cacheKey };
    }

    const compositeBounds = this.compositeBounds(minX, minY, maxX, maxY, zoom);
    const canvas = this.createCanvas(maxX - minX + 1, maxY - minY + 1);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }

    ctx.fillStyle = '#888888';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const loadPromises: Promise<void>[] = [];
    for (let ty = minY; ty <= maxY; ty++) {
      for (let tx = minX; tx <= maxX; tx++) {
        if (signal?.aborted) {
          break;
        }
        const url = `${this.baseUrl}/${layer}/default/current/3857/${zoom}/${tx}/${ty}.${this.format}`;
        const canvasX = (tx - minX) * TILE_SIZE;
        const canvasY = (ty - minY) * TILE_SIZE;

        loadPromises.push(
          this.loadImage(url, signal).then(img => {
            if (img) {
              ctx.drawImage(img, canvasX, canvasY, TILE_SIZE, TILE_SIZE);
            }
          }),
        );
      }
    }

    try {
      await Promise.all(loadPromises);
    } catch (e) {
      if (signal?.aborted || (e instanceof Error && e.name === 'AbortError')) {
        throw e;
      }
      console.warn('Some WMTS tiles failed to load', e);
    }

    if (signal?.aborted) {
      // Do not cache a partially stitched texture.
      return null;
    }

    const texture = this.createTexture(canvas);
    const entry: TextureCacheEntry = {
      cacheKey,
      texture,
      wmtsBounds: compositeBounds,
      refCount: 1,
      cached: generation === this.cacheGeneration,
    };
    this.liveEntries.set(texture, entry);
    // A request from before a layer switch/clear must not repopulate the cache.
    // Its caller still owns the returned reference and can release it by identity.
    if (entry.cached) {
      const replaced = this.cache.get(cacheKey);
      if (replaced) this.retire(replaced);
      this.cache.set(cacheKey, entry);
    }

    return { texture, wmtsBounds: compositeBounds, cacheKey };
  }

  /**
   * Take an additional reference on an already cached composite.
   */
  retain(cacheKey: string): void {
    const cached = this.cache.get(cacheKey);
    if (cached) {
      cached.refCount++;
    }
  }

  /**
   * Drop a reference taken by a tile that is being removed. Supply the texture
   * when a layer switch or overlapping load may have replaced the cached entry.
   */
  release(cacheKey: string, texture?: THREE.Texture): void {
    const entry = texture
      ? this.liveEntries.get(texture)
      : this.cache.get(cacheKey) ??
        Array.from(this.liveEntries.values()).find(candidate => candidate.cacheKey === cacheKey);
    if (!entry || entry.cacheKey !== cacheKey) return;
    entry.refCount = Math.max(0, entry.refCount - 1);
    this.disposeIfUnused(entry);
  }

  private retire(entry: TextureCacheEntry): void {
    entry.cached = false;
    this.disposeIfUnused(entry);
  }

  private disposeIfUnused(entry: TextureCacheEntry): void {
    if (!entry.cached && entry.refCount === 0 && this.liveEntries.delete(entry.texture)) {
      entry.texture.dispose();
    }
  }

  /**
   * Calculate UV coordinates for terrain vertices based on composite WMTS tile bounds
   */
  calculateTextureUVs(mesh: TerrainTileMesh, wmtsBounds: GeoBounds): void {
    const uvs = mesh.geometry.attributes.uv.array as Float32Array;
    const positions = mesh.userData.originalPositions;

    if (!positions) {
      console.warn('No original positions stored for UV calculation');
      return;
    }

    const wmtsLonRange = wmtsBounds.east - wmtsBounds.west;
    const wmtsLatRange = wmtsBounds.north - wmtsBounds.south;

    for (let i = 0; i < positions.length / 3; i++) {
      const lon = positions[i * 3];
      const lat = positions[i * 3 + 1];

      uvs[i * 2] = (lon - wmtsBounds.west) / wmtsLonRange;
      uvs[i * 2 + 1] = (lat - wmtsBounds.south) / wmtsLatRange;
    }

    mesh.geometry.attributes.uv.needsUpdate = true;
  }

  clear(): void {
    this.cacheGeneration++;
    this.cache.clear();
  }

  /**
   * Convert WGS84 lon/lat to Web Mercator tile coordinates
   */
  lonLatToWebMercatorTile(lon: number, lat: number, zoom: number): TileCoord {
    return SWISS_PROJECTION.lonLatToWebMercatorTile(lon, lat, zoom);
  }

  /**
   * Get WMTS tile bounds in WGS84
   */
  getWebMercatorTileBounds(x: number, y: number, zoom: number): GeoBounds {
    return SWISS_PROJECTION.webMercatorTileBounds(x, y, zoom);
  }

  /**
   * Load an image from URL with cancellation support and bounded concurrency.
   */
  async loadImage(url: string, signal?: AbortSignal): Promise<HTMLImageElement | null> {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    await this.acquireImageSlot();

    try {
      const response = await fetch(url, { signal });
      if (!response.ok) {
        console.warn(`Failed to load image: ${url} (${response.status})`);
        return null;
      }
      const blob = await response.blob();
      const bitmap = await createImageBitmap(blob);

      // drawImage accepts ImageBitmap, so the cast is safe for our only caller.
      return bitmap as unknown as HTMLImageElement;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw error;
      }
      console.warn(`Failed to load image: ${url}`, error);
      return null;
    } finally {
      this.releaseImageSlot();
    }
  }

  private compositeBounds(
    minX: number,
    minY: number,
    maxX: number,
    maxY: number,
    zoom: number,
  ): GeoBounds {
    const bounds: GeoBounds = {
      west: Infinity,
      south: Infinity,
      east: -Infinity,
      north: -Infinity,
    };

    for (let ty = minY; ty <= maxY; ty++) {
      for (let tx = minX; tx <= maxX; tx++) {
        const tb = this.getWebMercatorTileBounds(tx, ty, zoom);
        bounds.west = Math.min(bounds.west, tb.west);
        bounds.south = Math.min(bounds.south, tb.south);
        bounds.east = Math.max(bounds.east, tb.east);
        bounds.north = Math.max(bounds.north, tb.north);
      }
    }

    return bounds;
  }

  private createCanvas(tilesX: number, tilesY: number): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = tilesX * TILE_SIZE;
    canvas.height = tilesY * TILE_SIZE;
    return canvas;
  }

  private createTexture(canvas: HTMLCanvasElement): THREE.CanvasTexture {
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = true;
    texture.anisotropy = this.maxAnisotropy();
    texture.flipY = true;
    return texture;
  }

  private acquireImageSlot(): Promise<void> {
    if (this.activeImageLoads < WmtsTextureProvider.MAX_CONCURRENT_IMAGES) {
      this.activeImageLoads++;
      return Promise.resolve();
    }
    return new Promise(resolve => this.imageQueue.push(resolve));
  }

  private releaseImageSlot(): void {
    const next = this.imageQueue.shift();
    if (next) {
      next();
    } else {
      this.activeImageLoads = Math.max(0, this.activeImageLoads - 1);
    }
  }
}
